import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Scale, Sparkles } from "lucide-react";

export default function Auth({ mode = "login" }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success(isLogin ? t("auth.welcomeBack") : t("auth.accountCreated"));
      nav("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("auth.genericError"));
    } finally {
      setLoading(false);
    }
  };

  const values = [
    [Scale, t("auth.value1")],
    [ShieldCheck, t("auth.value2")],
    [Sparkles, t("auth.value3")],
  ];

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.05fr_1fr]">
      <div
        className="relative hidden flex-col justify-between overflow-hidden p-12 text-white lg:flex"
        style={{
          background:
            "radial-gradient(120% 120% at 15% 0%, hsl(223 100% 22%) 0%, hsl(var(--brand-900)) 45%, hsl(var(--brand-950)) 100%)",
        }}
      >
        <div className="grain absolute inset-0" />
        <div
          className="pointer-events-none absolute -right-24 top-1/3 h-96 w-96 rounded-full opacity-30 blur-3xl"
          style={{ background: "hsl(223 100% 45%)" }}
        />
        <div className="relative">
          <Logo dark />
        </div>
        <div className="relative max-w-md">
          <p className="mb-4 text-xs font-semibold uppercase tracking-overline text-white/50">
            {t("auth.overline")}
          </p>
          <h1 className="font-display text-5xl font-extrabold leading-[1.05] tracking-tight">
            {t("auth.slogan1")}
            <br />
            {t("auth.slogan2")}
          </h1>
          <p className="mt-6 text-base leading-relaxed text-white/70">{t("auth.brandBlurb")}</p>
          <div className="mt-10 space-y-4">
            {values.map(([Icon, txt]) => (
              <div key={txt} className="flex items-center gap-3 text-sm text-white/85">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10">
                  <Icon className="h-4 w-4" />
                </span>
                {txt}
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-white/40">{t("auth.copyright")}</div>
      </div>

      <div className="flex items-center justify-center bg-background p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="w-full max-w-sm"
        >
          <div className="mb-8 flex flex-col items-center text-center">
            <Logo align="col" showSubtitle size={46} />
            <p className="mt-5 font-display text-lg font-semibold tracking-tight">
              {t("auth.slogan1")} {t("auth.slogan2")}
            </p>
          </div>
          <h2 className="text-center font-display text-2xl font-extrabold tracking-tight">
            {isLogin ? t("auth.signInTitle") : t("auth.registerTitle")}
          </h2>
          <p className="mt-1.5 text-center text-sm text-muted-foreground">
            {isLogin ? t("auth.signInSubtitle") : t("auth.registerSubtitle")}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name">{t("auth.name")}</Label>
                <Input
                  id="name"
                  data-testid="auth-name-input"
                  className="mt-1.5"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
            )}
            <div>
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                data-testid="auth-email-input"
                className="mt-1.5"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Input
                id="password"
                type="password"
                data-testid="auth-password-input"
                className="mt-1.5"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
            </div>

            {isLogin && (
              <div className="flex items-center justify-between pt-1">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                  <Checkbox
                    checked={remember}
                    onCheckedChange={(v) => setRemember(!!v)}
                    data-testid="remember-me-checkbox"
                  />
                  {t("auth.rememberMe")}
                </label>
                <button
                  type="button"
                  onClick={() => toast.info(t("auth.forgotToast"))}
                  className="text-sm font-medium text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  {t("auth.forgotPassword")}
                </button>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:opacity-90"
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? t("common.pleaseWait") : isLogin ? t("auth.signInBtn") : t("auth.registerBtn")}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {isLogin ? t("auth.newToOrkeva") : t("auth.alreadyHaveAccount")}
            <Link
              to={isLogin ? "/register" : "/login"}
              className="font-semibold text-primary hover:underline"
              data-testid="auth-switch-link"
            >
              {isLogin ? t("auth.createAccountLink") : t("auth.signInLink")}
            </Link>
          </p>

          <p className="mt-8 text-center text-[11px] font-medium uppercase tracking-overline text-muted-foreground">
            {t("auth.tagline")}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
